import { EVENT_LOADED, EVENT_UNLOAD, eventsActions } from './events.era';
import { AllSameKeyValuesEnforcer, AllSameKeysEnforcer } from '../helpers/helpers';
import { tableLightColors } from '../helpers/table-helpers';
import { EntityState, createEntityAdapter } from '@ngrx/entity';
import { Action, createActionGroup, createFeature, createFeatureSelector, createReducer, emptyProps, on } from '@ngrx/store';
import { Performer, SocialLink, MediaTermState, performerProps } from 'globals/store/performers.data';
import { PublicEditProfileInfo, publicEditProfileStatusTypes, CreateEditProfileType, PublicEditProfileStatusTypesType, BriefStati, PerformerStati, SponsorStati, ReadyStati, PEPStati } from 'globals/store/share.data';
import { Ranking } from 'globals/store/events.data';
import { makeMapFromListOfObjectsAndKey } from 'globals/helpers/helpers';
import { TableLightColors } from 'globals/helpers/table-helpers.data';
export { Performer, SocialLink, PublicEditProfileInfo, publicEditProfileStatusTypes, performerProps }

// This file may well just be a source of models for the new performer module for now.
export const CHANGE_TABLE_FILTERS = "[Performers | Sponsors] Change table filters"


type idsForColors = PublicEditProfileStatusTypesType | MediaTermState
export const colorsByPublicEditProfile: { [k in idsForColors]: string } = { Active: tableLightColors.yellow, Submitted: tableLightColors.green, Off: tableLightColors.gray, Accepted: tableLightColors.green, 'Not Accepted': tableLightColors.yellow }

export function getPublicEditProfileInfo(type: CreateEditProfileType, entityId:string, shareables?:{[key:string]:PublicEditProfileInfo}){
  if (entityId && shareables && shareables[entityId]) return shareables[entityId]
  const profileInfo: PublicEditProfileInfo = { code: null, status: publicEditProfileStatusTypes.Off }
  switch (type) {
    case 'profile':
      profileInfo.performerId = entityId
      break
    case 'exhibitor':
      profileInfo.exhibitorId = entityId
      break
    case 'sponsor':
      profileInfo.sponsorId = entityId
      break
    case 'resource':
      profileInfo.resourceId = entityId
      break
    case 'attendee':
      profileInfo.attendeeId = entityId
      break
    default:
      console.log("Missing type for getPublicEditProfileInfo " + type)
      break
  }
  return profileInfo
}

interface SocialLinkNames { Facebook, Instagram, LinkedIn, Twitter, Threads, TikTok, YouTube, Web }
export const socialLinkNames: AllSameKeyValuesEnforcer<SocialLinkNames> = { Facebook: 'Facebook', Instagram: 'Instagram', LinkedIn: 'LinkedIn', Twitter: 'Twitter', Threads: 'Threads', TikTok: 'TikTok', YouTube: 'YouTube', Web: 'Web'}
export const socialLinkNamesList = Object.keys(socialLinkNames)

export interface PerformerDisplayTypes { readOnly, listOnly, editableCard, snapshot, staticList, staticCard, widePod, narrowPod, staticCardSmallHeader }
export type PerformerDisplayType = keyof PerformerDisplayTypes
export const performerDisplayTypes: AllSameKeyValuesEnforcer<PerformerDisplayTypes> = { readOnly: 'readOnly', listOnly: 'listOnly', editableCard: 'editableCard', snapshot: 'snapshot', staticList: 'staticList', staticCard: 'staticCard', widePod: 'widePod', narrowPod: 'narrowPod', staticCardSmallHeader: 'staticCardSmallHeader' }


export const performerStatus: AllSameKeyValuesEnforcer<PerformerStati> = { NotInvited: 'NotInvited', Accepted: 'Accepted', Pending: 'Pending', Declined: 'Declined' }

export const briefStatus: AllSameKeyValuesEnforcer<BriefStati> = { NotBriefed: 'NotBriefed', Briefed: 'Briefed' }



export interface ManyStatus extends PerformerStati, BriefStati, SponsorStati, ReadyStati, PEPStati {}

export const colorsByStatus: AllSameKeysEnforcer<ManyStatus> & {'Not Confirmed'?: string, 'In Progress'?: string, 'Review'?: string} = { Accepted: tableLightColors.green, Pending: tableLightColors.yellow, Declined: tableLightColors.red, NotInvited: tableLightColors.gray, NotBriefed: tableLightColors.gray, Briefed: tableLightColors.green, Confirmed: tableLightColors.green, Cancelled: tableLightColors.red, Ready: tableLightColors.green, 'NotReady': tableLightColors.gray, Active: tableLightColors.yellow, Submitted: tableLightColors.green, Off: tableLightColors.gray }
colorsByStatus['Not Confirmed'] = tableLightColors.gray
colorsByStatus['In Progress'] = tableLightColors.yellow;
colorsByStatus['Review'] = tableLightColors.red;

export const performerStatusNames: AllSameKeysEnforcer<ManyStatus> = { NotInvited: 'Not Invited', Accepted: 'Accepted', Pending: 'Pending', Declined: 'Declined', NotBriefed: 'Not Briefed', Briefed: 'Briefed', Cancelled: 'Cancelled', Confirmed: 'Confirmed', Ready: 'Ready', NotReady: 'Not Ready', Active: 'Active', Submitted: 'Submitted', Off: 'Off' }


export interface TableFilterTypes { NoFilters, PerformersTable, SessionsTable, SponsorsTable, SponsorSessionsTable, ExhibitorsTable, ExhibitorStandsTable, ResourcesTable, QuotesTable: 'QuotesTable', programTable, appTable, attendeesTable } // will change the filters used in the filtering function for Performers and Sessions AND Sponsors and SponsorSessions

export const tableFilterTypes: AllSameKeyValuesEnforcer<TableFilterTypes> = { NoFilters: 'NoFilters', PerformersTable: 'PerformersTable', SessionsTable: 'SessionsTable', SponsorsTable: 'SponsorsTable', SponsorSessionsTable: 'SponsorSessionsTable', ExhibitorsTable: 'ExhibitorsTable', ExhibitorStandsTable: 'ExhibitorStandsTable', ResourcesTable: 'ResourcesTable', QuotesTable: 'QuotesTable', programTable: 'programTable', appTable: 'appTable', attendeesTable: 'attendeesTable' }



export interface PerformersState extends EntityState<Performer> {
  performerFilterType: string
  performerTypes: { [id:string]: Ranking }
}


const getDefaultPerformersState = ():PerformersState => ({
  ids: [],
  entities: {},
  performerFilterType: tableFilterTypes.NoFilters,
  performerTypes: {}
})

export const adapter = createEntityAdapter<Performer>()
export const makeInitialState = (): PerformersState => adapter.getInitialState(getDefaultPerformersState())

// Actions 
export const performerActions = createActionGroup({
  source: 'Performer',
  events: {
    'Change Table Filters': (filterType: string) => ({ filterType }),
  }
})

export const PerformerReducer = createReducer(
  {...makeInitialState()},
  on(performerActions.changeTableFilters, (state, { filterType }) => ({ ...state, performerFilterType: filterType })),
  on(eventsActions.loadEvent, (state, { payload }) => {
    const updatedState = adapter.upsertMany(Object.values(payload.performers), state)
    const newState = { ...updatedState }
    newState.performerTypes = !payload.performerTypes ? {} : makeMapFromListOfObjectsAndKey(payload.performerTypes, 'id')
    return newState
  }),
  on(eventsActions.unloadEvent, (state) => ({ ...makeInitialState() })),
)

export const performersFeature = createFeature({
  name: 'performers',
  reducer: PerformerReducer,
});

export const performersStateSelector = createFeatureSelector<PerformersState>('performers');